import { useAuth } from 'context/auth-context';

import useGym from './useGym';

export interface OnboardingChecklistState {
  /** Checked state by step key, for the cards to look themselves up in. */
  checkedByKey: Record<string, boolean>;
  completedCount: number;
  /** Read off the response rather than hardcoded, so the count and the copy cannot disagree. */
  totalCount: number;
  isComplete: boolean;
  bannerDismissed: boolean;
  /**
   * Whether to render the banner at all. True while the checklist is incomplete, or complete
   * and not yet dismissed. Unchecking a step after dismissal drops `isComplete` and brings the
   * incomplete banner back, without ever clearing `bannerDismissed`.
   */
  shouldRenderBanner: boolean;
  gymId?: string | null;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Turns a camelized step field from the gym response back into the snake_case key the toggle
 * action takes and `steps.ts` keys its cards by.
 */
const toStepKey = (field: string): string => field.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);

/**
 * Reads the gym's onboarding checklist off the gym query the app already runs.
 *
 * Progress needs no endpoint of its own; it rides along on `GET /gyms/{id}/`, so the banner
 * and the checklist page read one cache entry and can never show different counts.
 */
export default function useOnboardingChecklist(): OnboardingChecklistState {
  const { session } = useAuth();
  const gymId = session?.gym?.gymId;
  const { data, isLoading, isError } = useGym(gymId);

  const { bannerDismissed = false, ...stepFields } = data?.data?.onboardingChecklist ?? {};

  const checkedByKey = Object.entries(stepFields).reduce<Record<string, boolean>>(
    (acc, [field, checked]) => {
      acc[toStepKey(field)] = Boolean(checked);
      return acc;
    },
    {},
  );

  const completedCount = Object.values(stepFields).filter(Boolean).length;
  const totalCount = Object.keys(stepFields).length;
  const isComplete = totalCount > 0 && completedCount === totalCount;

  return {
    checkedByKey,
    completedCount,
    totalCount,
    isComplete,
    bannerDismissed,
    shouldRenderBanner: totalCount > 0 && !(isComplete && bannerDismissed),
    gymId,
    isLoading,
    isError,
  };
}
